/**
 * The kidneyProfile the funnel writes for a new account. Pure (imports only
 * appLimits + profileWeight), so `npm test` compiles it (tsconfig.test.json).
 *
 * S1 (2026-10-24): the profile carries the same limit fields as an account made
 * in the app (`services/onboarding/onboardingService.ts`): `calculatedLimits`
 * only. The funnel used to copy them into the legacy `dailySodiumLimit` /
 * `dailyPotassiumLimit` / `dailyPhosphorusLimit` / `dailyFluidLimit` /
 * `dailyProteinLimit` fields, which the app, the scorer and the meal prompt read
 * as limits the USER entered ("user-entered fluid limit", the potassium
 * relaxation of stone-only users switched off). Fluid and protein are now
 * worked out by the app from the condition, the dialysis type and the weight.
 */
import { appNutrientLimits, toAppCondition } from './appLimits'
import { weightProfileFields, type WeightAnswers } from './profileWeight'

export interface ProfileAnswers extends WeightAnswers {
  kidneyStatus: string | null
  comorbidities?: string[] | null
  dialysisType: string | null
  primaryGoal: string | null
}

export type AppDialysisType = 'hemodialysis' | 'peritoneal' | 'home_hemo' | 'not_started'

/**
 * Map funnel dialysis type to app format. 4.41: the app knows all four onboarding values
 * (Settings → Kidney Status has a picker, the server prompt labels each), so they pass
 * through as they are — `home_hemo` is no longer folded into `hemodialysis`, and
 * `not_started` is no longer dropped. Stored only for an On Dialysis answer, so a type
 * answered before going back and changing the status never lands on a non-dialysis profile.
 */
export function mapDialysisType(type: string | null, kidneyStatus: string | null): AppDialysisType | null {
  if (kidneyStatus !== 'on_dialysis') return null
  if (type === 'hemodialysis' || type === 'peritoneal' || type === 'home_hemo' || type === 'not_started') return type
  return null
}

/** Build kidneyProfile — undefined values are omitted so Firestore doesn't store them. */
export function funnelKidneyProfile(answers: ProfileAnswers): Record<string, unknown> {
  const condition = toAppCondition(answers.kidneyStatus)
  const comorbidities = (answers.comorbidities || []).filter(c => c !== 'none')
  // The app's own defaults (appLimits.ts, parity-tested): Starting soon gets the Stage 5
  // row, peritoneal dialysis potassium 3,000 mg.
  const dialysisType = mapDialysisType(answers.dialysisType, answers.kidneyStatus)
  const calculatedLimits = appNutrientLimits(condition, comorbidities, dialysisType)

  const kidneyProfile: Record<string, unknown> = {
    condition,
    primaryGoal: answers.primaryGoal || 'manage_ckd',
    comorbidities,
    onDialysis: answers.kidneyStatus === 'on_dialysis',
    calculatedLimits,
  }
  if (dialysisType) kidneyProfile.dialysisType = dialysisType
  // weightUnit only when the buyer picked one on the weight step (4.39 F6).
  Object.assign(kidneyProfile, weightProfileFields(answers))
  return kidneyProfile
}
